import { Alert } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { AssetRenderComponentProps } from '../assetTypes'
import ConfigAssetRenderer from '../configs/ConfigAssetRenderer'
import {
  applyExperimentPartitionToConfigContent,
  parseExperimentDefinition,
  pickExperimentPartition,
  resolveExperimentPartitions,
  type ResolvedExperimentPartition,
} from './experimentDefinition'

const normalizeName = (value: string) => value.trim().toLowerCase()

const ExperimentAssetRenderer = ({ content, trellis, onLog }: AssetRenderComponentProps) => {
  const resolution = useMemo(() => {
    try {
      const definition = parseExperimentDefinition(content)
      const partitions = resolveExperimentPartitions(definition)
      return { definition, partitions, error: null }
    } catch (error) {
      return {
        definition: null,
        partitions: [] as ResolvedExperimentPartition[],
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }, [content])

  const [randomPartitionName, setRandomPartitionName] = useState<string | null>(null)

  useEffect(() => {
    if (!resolution.definition) {
      setRandomPartitionName(null)
      return
    }

    if (resolution.definition.partition) {
      setRandomPartitionName(null)
      return
    }

    const partitionNames = new Set(resolution.partitions.map((partition) => partition.name))

    setRandomPartitionName((previous) => {
      if (previous && partitionNames.has(previous)) return previous
      return pickExperimentPartition(resolution.definition).name
    })
  }, [resolution.definition, resolution.partitions])

  const selectedResolvedPartition = useMemo(() => {
    if (!resolution.definition) return undefined
    const targetName = resolution.definition.partition ?? randomPartitionName
    if (!targetName) return undefined
    return resolution.partitions.find((partition) => normalizeName(partition.name) === normalizeName(targetName))
  }, [randomPartitionName, resolution.definition, resolution.partitions])

  const configHandle = useMemo(
    () => (resolution.definition && trellis ? trellis.assets.get(resolution.definition.config) : null),
    [resolution.definition, trellis]
  )

  const mutatedConfig = useMemo(() => {
    if (!resolution.definition || !selectedResolvedPartition) {
      return { content: null, error: null as string | null }
    }
    if (!configHandle) {
      return { content: null, error: 'Experiment runtime bridge is unavailable.' }
    }
    if (typeof configHandle.text !== 'string' || configHandle.text.trim().length === 0) {
      return { content: null, error: 'Referenced config asset is unavailable or has no text payload.' }
    }

    try {
      return {
        content: applyExperimentPartitionToConfigContent(configHandle.text, selectedResolvedPartition),
        error: null as string | null,
      }
    } catch (error) {
      return {
        content: null,
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }, [configHandle, resolution.definition, selectedResolvedPartition])

  useEffect(() => {
    if (!resolution.error) return
    onLog?.(`Experiment parse error: ${resolution.error}`)
  }, [onLog, resolution.error])

  useEffect(() => {
    if (!mutatedConfig.error) return
    onLog?.(`Experiment preview error: ${mutatedConfig.error}`)
  }, [mutatedConfig.error, onLog])

  if (!trellis) {
    return <Alert severity="error">Experiment runtime bridge is unavailable.</Alert>
  }

  if (resolution.error || !resolution.definition) {
    return <Alert severity="error">{resolution.error ?? 'Experiment payload could not be resolved.'}</Alert>
  }

  if (resolution.partitions.length === 0) {
    return <Alert severity="error">Experiment must define at least one partition.</Alert>
  }

  if (mutatedConfig.error || !mutatedConfig.content || !selectedResolvedPartition) {
    return <Alert severity="error">{mutatedConfig.error ?? 'Experiment preview could not be prepared.'}</Alert>
  }

  return (
    <ConfigAssetRenderer
      content={mutatedConfig.content}
      trellis={trellis}
      renderData={undefined}
      layout="fill"
      showLog={false}
      onLog={onLog}
    />
  )
}

export default ExperimentAssetRenderer
